import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { Ec2Instance, AccountCost, InstanceDetailMetrics, LambdaAccountData, RdsInstance, LambdaFunctionMetrics, RdsDetailMetrics, CostHistoryData } from './types';
import type { AlertHistoryEntry, LogGroup, LogEvent } from './types';

export const awsHealthMonitorApiRef = createApiRef<AwsHealthMonitorApi>({
  id: 'plugin.aws-health-monitor.service',
});

export interface AccountMeta {
  accountId: string;
  name: string;
}

export interface AwsHealthData {
  instances: Ec2Instance[];
  accountCosts: Record<string, AccountCost>;
  accounts: AccountMeta[];
}

export interface AwsHealthMonitorApi {
  getHealthData(): Promise<AwsHealthData>;
  getInstanceMetrics(instanceId: string, accountId: string, region: string, hours?: number): Promise<InstanceDetailMetrics>;
  getLambdaData(): Promise<Record<string, LambdaAccountData>>;
  getLambdaFunctionMetrics(functionName: string, accountId: string, hours?: number): Promise<LambdaFunctionMetrics>;
  getRdsData(): Promise<Record<string, RdsInstance[]>>;
  getRdsInstanceMetrics(instanceId: string, accountId: string, hours?: number): Promise<RdsDetailMetrics>;
  getAlertHistory(): Promise<AlertHistoryEntry[]>;
  getLogGroups(instanceId: string, accountId: string): Promise<LogGroup[]>;
  getLogs(logGroupName: string, accountId: string, instanceId: string): Promise<LogEvent[]>;
  getCostHistory(months: number): Promise<CostHistoryData>;
}

export class AwsHealthMonitorClient implements AwsHealthMonitorApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getHealthData(): Promise<AwsHealthData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const response = await this.fetchApi.fetch(`${baseUrl}/ec2`);
    if (!response.ok) {
      throw new Error(`AWS Health Monitor API error: ${response.statusText}`);
    }
    const data = await response.json();
    return {
      instances:    data.instances    as Ec2Instance[],
      accountCosts: data.accountCosts as Record<string, AccountCost>,
      accounts:     (data.accounts ?? []) as AccountMeta[],
    };
  }

  async getInstanceMetrics(instanceId: string, accountId: string, region: string, hours = 24): Promise<InstanceDetailMetrics> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const params = new URLSearchParams({ instanceId, accountId, region, hours: String(hours) });
    const response = await this.fetchApi.fetch(`${baseUrl}/ec2/instance-metrics?${params}`);
    if (!response.ok) {
      throw new Error(`AWS Health Monitor metrics error: ${response.statusText}`);
    }
    return response.json();
  }

  async getLambdaData(): Promise<Record<string, LambdaAccountData>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const response = await this.fetchApi.fetch(`${baseUrl}/lambda`);
    if (!response.ok) {
      throw new Error(`Lambda data error: ${response.statusText}`);
    }
    return response.json();
  }

  async getLambdaFunctionMetrics(functionName: string, accountId: string, hours = 24): Promise<LambdaFunctionMetrics> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const params = new URLSearchParams({ functionName, accountId, hours: String(hours) });
    const response = await this.fetchApi.fetch(`${baseUrl}/lambda/function-metrics?${params}`);
    if (!response.ok) {
      throw new Error(`Lambda function metrics error: ${response.statusText}`);
    }
    return response.json();
  }

  async getRdsData(): Promise<Record<string, RdsInstance[]>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const response = await this.fetchApi.fetch(`${baseUrl}/rds`);
    if (!response.ok) {
      throw new Error(`RDS data error: ${response.statusText}`);
    }
    return response.json();
  }

  async getRdsInstanceMetrics(instanceId: string, accountId: string, hours = 24): Promise<RdsDetailMetrics> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const params = new URLSearchParams({ instanceId, accountId, hours: String(hours) });
    const response = await this.fetchApi.fetch(`${baseUrl}/rds/instance-metrics?${params}`);
    if (!response.ok) {
      throw new Error(`RDS instance metrics error: ${response.statusText}`);
    }
    return response.json();
  }

  async getCostHistory(months: number): Promise<CostHistoryData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-billing');
    const response = await this.fetchApi.fetch(`${baseUrl}/billing/summary?months=${months}`);
    if (!response.ok) {
      throw new Error(`Cost history error: ${response.statusText}`);
    }
    return response.json();
  }

  async getAlertHistory(): Promise<AlertHistoryEntry[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const response = await this.fetchApi.fetch(`${baseUrl}/alerts/history`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.entries as AlertHistoryEntry[];
  }

  async getLogGroups(instanceId: string, accountId: string): Promise<LogGroup[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const params = new URLSearchParams({ instanceId, accountId });
    const response = await this.fetchApi.fetch(`${baseUrl}/ec2/log-groups?${params}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.logGroups as LogGroup[];
  }

  async getLogs(logGroupName: string, accountId: string, instanceId: string): Promise<LogEvent[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('aws-health-monitor');
    const params = new URLSearchParams({ logGroupName, accountId, instanceId });
    const response = await this.fetchApi.fetch(`${baseUrl}/ec2/logs?${params}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.events as LogEvent[];
  }
}
